import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { rejects } from 'assert';
import { filter, ReplaySubject, Subscription, take } from 'rxjs';
import { NotificationModel } from 'src/app/notification/models/notification';
import { NOTIFICATION } from 'src/app/notification/tokens';
import { CheckoutConfiguration } from 'src/business/models/main-configuration.model';
import { Address, CustomerType } from 'src/business/services/client.generated';
import { CvrEanLookupComponent } from 'src/commerce/cvr-ean-lookup/cvr-ean-lookup.component';
import { InputDirective } from 'src/commerce/forms/input.directive';
import { InputComponent } from 'src/commerce/forms/input/input.component';
import { StandardForms } from 'src/commerce/forms/standard-inputs';
import { PageBase } from 'src/core-lib/navigation/base-page';
import { ComponentPage } from 'src/core-lib/navigation/models/component-page.model';
import { Page } from 'src/core-lib/navigation/models/page.interface';
import { UmbracoGrid } from 'src/layout/grid/models/umbraco-grid.type';
import { StepComponent } from 'src/layout/step/step.component';
import { StepperComponent } from 'src/layout/stepper/stepper.component';
import { MarketSegment } from 'src/utils/market-segment.util';
import { ContentCacheUtils, StorageUtil } from 'src/utils/storage.util';
import { CheckoutPageModule } from '../../checkout-page.module';
import { BillingAddressComponent } from '../../components/billing-address/billing-address.component';
import { MarketSegmentComponent } from '../../components/market-segment/market-segment.component';
import { ShoppingAsComponent } from '../../components/shopping-as/shopping-as.component';
import { BasketService, PaymentMethods } from '../../services/basket.service';
import { MapToGA4 } from 'src/business/Mapper/map-to-ga4-item';
import { GA4 } from 'src/externals/tag-manager/models/ga4.model';






interface CheckoutPage extends Page {
    basketLink: string;
}

@Component({
    selector: 'app-checkout-page',
    standalone: true,
    imports: [
        CommonModule,
        CheckoutPageModule,
        StepperComponent,
        StepComponent,
        MarketSegmentComponent,
        CvrEanLookupComponent,
        BillingAddressComponent,
        InputComponent,
        InputDirective,
        ShoppingAsComponent,
    ],
    templateUrl: './checkout-page.component.html',
    styleUrls: ['./checkout-page.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class CheckoutPageComponent extends PageBase implements ComponentPage, OnDestroy {
    nextLink?: URL;
    currentStep?: StepComponent;
    page: CheckoutPage;
    checkoutType: string;

    checkout: Partial<CheckoutConfiguration>;
    customerInfo: () => Promise<boolean>;
    @ViewChild(StepperComponent, { static: true })
    private stepper: StepperComponent;

    privateSubmit: () => false;
    privateAddressForm: StandardForms.FormModel[];
    businessSubmit: () => false;
    businessAddressForm: StandardForms.FormModel[];
    organizationSubmit: () => false;
    organizationAddressForm: StandardForms.FormModel[];
    initialShoppingAs: string;
    shipmentSubmit: () => false;
    shippingForm: { legend: string; inputs: StandardForms.InputModel[]; }[];
    paymentSubmit: () => false;
    paymentForm: { legend: string; inputs: StandardForms.InputModel[]; }[];


    paymentTranslateKey = 'PAYMENT.EX_VAT';
    shippingTranslateKey = 'SHIPPING.EX_VAT';
    basketLink: string;
    currentLink: string;
    termsInput$: Promise<StandardForms.InputModel>;
    newsInput$: Promise<StandardForms.InputModel>;
    termsLayout: UmbracoGrid;
    subsscription: Subscription;
    constructor(
        @Inject(NOTIFICATION)
        private notificationService: ReplaySubject<NotificationModel>,
        private basketService: BasketService,
        private translateService: TranslateService,
        private _detector: ChangeDetectorRef,
        route: ActivatedRoute,
    ) {
        super(route);
        this.checkout = ContentCacheUtils.checkout<CheckoutConfiguration>();
        var basket = this.basketService.basket;
        this.subsscription = this.basketService.basket$.pipe(filter(_ => _ != null), take(1)).subscribe(basket => {
            queueMicrotask(() =>
                GA4.track(MapToGA4.checkoutStart(basket))
            )
        })
        if (basket?.customer === CustomerType.Public)
            this.initialShoppingAs = 'organization';
        else if (basket?.customer === CustomerType.Company)
            this.initialShoppingAs = 'business';
        else
            this.initialShoppingAs = 'private';

        this.checkoutType = this.initialShoppingAs;
        this.basketLink = Object.entries(StorageUtil.get<{ [key: string]: string }>('routing') ?? {}).find(([path, alias]) => alias == 'basketPage')[0];
        this.currentLink = window.location.pathname;
        this.termsInput$ = StandardForms.Input.Terms(this.translateService);
        this.newsInput$ = StandardForms.Input.News(this.translateService);

        this.privateSubmit = () => {
            StandardForms.SumbmitFunctions.submitAddressPrivate(
                window['address-form'],
                this.basketService,
                this.notificationService,
                this.stepper,
            );
            return false;
        };


        this.businessSubmit = () => {
            StandardForms.SumbmitFunctions.submitAddressBusiness(
                window['address-form'],
                this.basketService,
                this.notificationService,
                this.stepper,
            );
            return false;
        };


        this.organizationSubmit = () => {
            StandardForms.SumbmitFunctions.submitAddressOrganization(
                window['address-form'],
                this.basketService,
                this.notificationService,
                this.stepper,
            );
            return false;
        };

        this.shipmentSubmit = () => {
            StandardForms.SumbmitFunctions.submitShipment(
                window['shipment-form'],
                this.basketService,
                this.notificationService,
                this.stepper,
            )
            return false;
        }

        this.paymentSubmit = () => {
            StandardForms.SumbmitFunctions.submitPayment(
                window['payment-form'],
                this.basketService,
                this.notificationService,
                this.stepper,
            )
            return false;
        }
        this._detector.markForCheck();
    }

    async updateAddress() {
        var basket = this.basketService.basket;
        if (basket == null)
            return setTimeout(() => this.updateAddress(), 200);
        this.privateAddressForm = StandardForms.Forms.BillingPrivateAddress(
            basket.billingAddress,
            basket.note
        );
        this.businessAddressForm = StandardForms.Forms.BillingBusinessAddress(
            basket.billingAddress,
            basket.note,
            StandardForms.Mapper.cvr(basket),
            StandardForms.Mapper.contactAddress(basket),

        );
        this.organizationAddressForm = StandardForms.Forms.BillingOrganizationAddress(
            basket.billingAddress,
            basket.note,
            StandardForms.Mapper.cvr(basket),
            StandardForms.Mapper.contactAddress(basket),

        );
        this._detector.markForCheck();
    }

    async updateShippingMethods() {
        this.shippingForm = await StandardForms
            .Forms
            .Shipping(this.basketService);
        this._detector.markForCheck();
    }
    async updatePaymentForm() {
        this.paymentForm = await StandardForms
            .Forms
            .Payment(this.basketService, this.translateService);
        this._detector.markForCheck();
    }

    setAddress(address: Address) {
        StandardForms.UpdateForm.setAddress(window['address-form'], address);
        this._detector.markForCheck();
    }

    setVat(vat: string) {
        StandardForms.UpdateForm.cvrOrGln(window['address-form'], vat);
        this._detector.markForCheck();
    }
    async openTerms() {
        window['terms-modal'].showModal();
        this.termsLayout = await new Promise<UmbracoGrid>(async (resolve, reject) => {
            var emptyResponse = [
                {
                    "settings": {
                        "colorTheme": {
                            "color": "#000000",
                            "backgroundColor": ""
                        },
                        "maxWidth": "1260",
                        "margin": "0",
                        "size": "size-60",
                        "tag": "H2",
                        "colorThemePost": {
                            "color": "#cc0000",
                            "backgroundColor": ""
                        },
                        "alias": "headlineSettings"
                    },
                    "text": this.translateService.instant("CHECKOUT.EMPTY_TERMS1"),
                    "textPost": this.translateService.instant("CHECKOUT.EMPTY_TERMS2"),
                    "alias": "headlineElement"
                }
            ] as UmbracoGrid;
            var checkoutData = StorageUtil.get<{ termslink: string }>('checkout');
            if (checkoutData == null || checkoutData.termslink == null)
                resolve(emptyResponse);
            let termsLink = new URL(checkoutData.termslink, window.location.origin);
            termsLink.searchParams.set('type', 'json');
            let resp = await fetch(termsLink.href).catch(e => {
                console.error(e);
                resolve(emptyResponse)
            });
            if (resp == null)
                resolve(emptyResponse);
            let data = await (resp as Response).json().catch(e => {
                console.error(e);
                resolve(emptyResponse)
            });
            if (data == null || data?.layout == null)
                resolve(emptyResponse);
            queueMicrotask(() => this._detector.markForCheck());
            resolve(data.layout as UmbracoGrid)
        })
    }

    ngOnDestroy(): void {
        super.ngOnDestroy();
        if (this.subsscription != null && !this.subsscription.closed)
            this.subsscription.unsubscribe()
    }
}





